import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getSocietyAdminData,
  getAdminSocietyDetail,
  getBillCategories,
  submitAdminReceivedCategoryPayment,
  submitAdminReceivedMultiCategoryPayment,
  AdminSocietyFlat,
  BillCategoryItem,
} from '../services/api';
import { HiExclamation, HiRefresh } from '../components/Icons';
import './SocietyAdminRecordPayment.css';

const PAYMENT_MODES = ['cash', 'cheque', 'UPI', 'NEFT'] as const;

interface CategoryAllocation {
  bill_type: string;
  amount: string;
}

const SocietyAdminRecordPayment: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [flats, setFlats] = useState<AdminSocietyFlat[]>([]);
  const [categories, setCategories] = useState<BillCategoryItem[]>([]);

  const [flatSearch, setFlatSearch] = useState('');
  const [selectedFlatId, setSelectedFlatId] = useState<number | null>(null);
  const [paymentMode, setPaymentMode] = useState<string>('cash');
  const [transactionId, setTransactionId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().split('T')[0]!);
  const [multiCategory, setMultiCategory] = useState(false);
  const [singleBillType, setSingleBillType] = useState('');
  const [singleAmount, setSingleAmount] = useState('');
  const [allocations, setAllocations] = useState<CategoryAllocation[]>([
    { bill_type: '', amount: '' },
  ]);

  useEffect(() => {
    if (!societyId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [detailRes, catRes] = await Promise.all([
          getAdminSocietyDetail(societyId),
          getBillCategories(societyId),
        ]);
        if (detailRes.success && detailRes.data?.flats) {
          setFlats(detailRes.data.flats);
        }
        if (catRes.success && catRes.data) {
          const active = catRes.data.filter((c) => c.is_active === 1);
          setCategories(active);
          if (active.length > 0) {
            const first = active[0]!.bill_type_key;
            setSingleBillType(first);
            setAllocations([{ bill_type: first, amount: '' }]);
          }
        }
      } catch (err: any) {
        setAlert({ type: 'error', message: err.message || 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [societyId]);

  const filteredFlats = useMemo(() => {
    if (!flatSearch.trim()) return flats;
    const q = flatSearch.toLowerCase();
    return flats.filter(
      (f) =>
        f.flat_number.toLowerCase().includes(q) ||
        (f.block_name && f.block_name.toLowerCase().includes(q))
    );
  }, [flats, flatSearch]);

  const selectedFlat = useMemo(
    () => flats.find((f) => f.id === selectedFlatId) ?? null,
    [flats, selectedFlatId]
  );

  const flatLabel = (flat: AdminSocietyFlat) =>
    flat.block_name ? `${flat.block_name} · ${flat.flat_number}` : flat.flat_number;

  const updateAllocation = (index: number, field: keyof CategoryAllocation, value: string) => {
    setAllocations((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addAllocationRow = () => {
    setAllocations((prev) => [
      ...prev,
      { bill_type: categories[0]?.bill_type_key ?? '', amount: '' },
    ]);
  };

  const removeAllocationRow = (index: number) => {
    setAllocations((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const resetForm = () => {
    setTransactionId('');
    setRemarks('');
    setSingleAmount('');
    setAllocations([{ bill_type: categories[0]?.bill_type_key ?? '', amount: '' }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !selectedFlatId) {
      setAlert({ type: 'error', message: 'Please select a flat.' });
      return;
    }

    const paidAt = paidDate ? `${paidDate}T12:00:00.000Z` : undefined;

    setSubmitting(true);
    setAlert(null);
    try {
      if (!multiCategory) {
        const amount = parseFloat(singleAmount);
        if (!singleBillType || !Number.isFinite(amount) || amount <= 0) {
          setAlert({ type: 'error', message: 'Enter a valid category and amount.' });
          return;
        }
        const res = await submitAdminReceivedCategoryPayment({
          flat_id: selectedFlatId,
          society_id: societyId,
          bill_type: singleBillType,
          amount,
          payment_mode: paymentMode,
          transaction_id: transactionId.trim() || undefined,
          remarks: remarks.trim() || undefined,
          paid_at: paidAt,
        });
        setAlert({ type: 'success', message: res.message || 'Payment recorded and approved.' });
      } else {
        const parsed = allocations
          .map((a) => ({ bill_type: a.bill_type, amount: parseFloat(a.amount) }))
          .filter((a) => a.bill_type && Number.isFinite(a.amount) && a.amount > 0);
        if (!parsed.length) {
          setAlert({ type: 'error', message: 'Add at least one category with a valid amount.' });
          return;
        }
        const res = await submitAdminReceivedMultiCategoryPayment({
          flat_id: selectedFlatId,
          society_id: societyId,
          allocations: parsed,
          payment_mode: paymentMode,
          transaction_id: transactionId.trim() || undefined,
          remarks: remarks.trim() || undefined,
          paid_at: paidAt,
        });
        setAlert({ type: 'success', message: res.message || 'Payment recorded and approved.' });
      }
      resetForm();
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to record payment' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!societyId) {
    return (
      <div className="sarp-page">
        <div className="recon-alert recon-alert-error">
          <HiExclamation size={18} /> No society selected.
        </div>
      </div>
    );
  }

  return (
    <div className="sarp-page">
      <div className="sarp-header">
        <div>
          <h2>Record counter payment</h2>
          <p className="sarp-subtitle">
            Record cash, cheque, or bank payments received at the society office. Payments are
            auto-approved.
          </p>
        </div>
      </div>

      {alert && (
        <div
          className={`recon-alert ${alert.type === 'success' ? 'recon-alert-success' : 'recon-alert-error'}`}
        >
          {alert.message}
          {alert.type === 'success' && selectedFlatId && (
            <>
              {' '}
              <Link to={`/society/flat-history?flatId=${selectedFlatId}`}>View flat ledger</Link>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="sarp-loading">
          <HiRefresh size={28} className="spin" />
          <p>Loading…</p>
        </div>
      ) : (
        <form className="sarp-form" onSubmit={(e) => void handleSubmit(e)}>
          <section className="sarp-section">
            <h3>Select flat</h3>
            <input
              type="search"
              className="sarp-input"
              placeholder="Search by block or flat number…"
              value={flatSearch}
              onChange={(e) => setFlatSearch(e.target.value)}
            />
            <div className="sarp-flat-list">
              {filteredFlats.map((flat) => (
                <button
                  key={flat.id}
                  type="button"
                  className={`sarp-flat-btn ${selectedFlatId === flat.id ? 'selected' : ''}`}
                  onClick={() => setSelectedFlatId(flat.id)}
                >
                  {flatLabel(flat)}
                </button>
              ))}
              {filteredFlats.length === 0 && (
                <p className="sarp-empty">No flats match your search.</p>
              )}
            </div>
            {selectedFlat && (
              <p className="sarp-selected-flat">Selected: {flatLabel(selectedFlat)}</p>
            )}
          </section>

          <section className="sarp-section">
            <div className="sarp-section-head">
              <h3>Payment details</h3>
              <label className="sarp-toggle">
                <input
                  type="checkbox"
                  checked={multiCategory}
                  onChange={(e) => setMultiCategory(e.target.checked)}
                />
                Split across categories
              </label>
            </div>

            {!multiCategory ? (
              <div className="sarp-grid">
                <label>
                  <span>Category</span>
                  <select
                    className="sarp-input"
                    value={singleBillType}
                    onChange={(e) => setSingleBillType(e.target.value)}
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.bill_type_key}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Amount (₹)</span>
                  <input
                    type="number"
                    className="sarp-input"
                    min="0"
                    step="0.01"
                    value={singleAmount}
                    onChange={(e) => setSingleAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </label>
              </div>
            ) : (
              <div className="sarp-allocations">
                {allocations.map((row, index) => (
                  <div key={index} className="sarp-allocation-row">
                    <select
                      className="sarp-input"
                      value={row.bill_type}
                      onChange={(e) => updateAllocation(index, 'bill_type', e.target.value)}
                    >
                      {categories.map((c) => (
                        <option key={c.id} value={c.bill_type_key}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      className="sarp-input"
                      min="0"
                      step="0.01"
                      value={row.amount}
                      onChange={(e) => updateAllocation(index, 'amount', e.target.value)}
                      placeholder="Amount"
                    />
                    <button
                      type="button"
                      className="sarp-remove-btn"
                      onClick={() => removeAllocationRow(index)}
                      disabled={allocations.length <= 1}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="recon-btn recon-btn-outline" onClick={addAllocationRow}>
                  Add category
                </button>
              </div>
            )}

            <div className="sarp-grid sarp-grid-3">
              <label>
                <span>Payment mode</span>
                <select
                  className="sarp-input"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Paid date</span>
                <input
                  type="date"
                  className="sarp-input"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                />
              </label>
              <label>
                <span>Transaction ID (optional)</span>
                <input
                  type="text"
                  className="sarp-input"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="UPI ref, cheque no."
                />
              </label>
            </div>

            <label className="sarp-full">
              <span>Remarks (optional)</span>
              <textarea
                className="sarp-input sarp-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                placeholder="Notes for this payment"
              />
            </label>
          </section>

          <div className="sarp-actions">
            <button
              type="submit"
              className="recon-btn recon-btn-primary"
              disabled={submitting || !selectedFlatId}
            >
              {submitting ? 'Recording…' : 'Record & approve payment'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SocietyAdminRecordPayment;
